/**
 * DataTable Component
 *
 * @module shared/components/data-table/DataTable
 *
 * Generic data table with sorting support, loading states, and empty states.
 * Can be used with any data type by providing column definitions.
 *
 * @example
 * ```tsx
 * <DataTable
 *   columns={columns}
 *   rows={cases}
 *   onSort={handleSort}
 *   sortColumn={sortColumn}
 *   sortOrder={sortOrder}
 *   loading={isLoading}
 * />
 * ```
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ComponentType, type FocusEvent, type MouseEvent, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import useMediaQuery from '@mui/material/useMediaQuery';
import CheckBoxOutlineBlankRoundedIcon from '@mui/icons-material/CheckBoxOutlineBlankRounded';
import CheckBoxRoundedIcon from '@mui/icons-material/CheckBoxRounded';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import type { TableRowProps } from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Typography from '@mui/material/Typography';
import UnfoldMoreRoundedIcon from '@mui/icons-material/UnfoldMoreRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import { alpha, useTheme } from '@mui/material/styles';

import { TableSkeleton } from '../loaders';

// ============================================================================
// Types
// ============================================================================

export interface ColumnDef<T = any> {
  /**
   * Unique key for the column
   */
  key: string;
  /**
   * Column header label
   */
  label: string;
  /**
   * Whether the column is sortable
   * @default false
   */
  sortable?: boolean;
  /**
   * Text alignment
   * @default 'left'
   */
  align?: 'left' | 'right' | 'center';
  /**
   * Custom cell renderer
   * @param value - The cell value
   * @param row - The entire row data
   * @returns React node to render
   */
  render?: (value: unknown, row: T) => React.ReactNode;
  /**
   * Minimum width for the column
   */
  minWidth?: number;
  /**
   * Maximum width for the column
   */
  maxWidth?: number;
  /**
   * Hide cell content when row is not hovered.
   * Content is still rendered (preserves column width) but invisible until hover.
   */
  showOnHoverOnly?: boolean;
}

export interface SelectionConfig<TId = any, TRow = any> {
  /**
   * Whether selection is enabled
   */
  enabled: boolean;
  /**
   * Function to get row ID from row data
   */
  getRowId?: (row: TRow) => TId;
  /**
   * Predicate to determine whether a given row is selectable.
   * If omitted, all rows are selectable.
   */
  isRowSelectable?: (row: TRow) => boolean;
  /**
   * Set of selected row IDs
   */
  selectedIds: Set<TId>;
  /**
   * Whether all (selectable) rows are selected
   */
  isAllSelected: boolean;
  /**
   * Callback when select all checkbox is toggled
   */
  onToggleAll: () => void;
  /**
   * Callback when a single row checkbox is toggled
   */
  onToggleOne: (id: TId) => void;
  /**
   * Render inline row actions shown on hover, next to the checkbox in the first cell.
   * No separate column is added — actions appear as an overlay alongside the checkbox.
   */
  rowActions?: (row: TRow) => ReactNode;
}

export interface DataTableProps<T = any, TId = any> {
  /**
   * Column definitions
   */
  columns: ColumnDef<T>[];
  /**
   * Data rows
   */
  rows: T[];
  /**
   * Sort handler (called when sortable column header is clicked)
   */
  onSort?: (column: string) => void;
  /**
   * Currently sorted column key
   */
  sortColumn?: string;
  /**
   * Current sort order
   */
  sortOrder?: 'asc' | 'desc';
  /**
   * Loading state
   * @default false
   */
  loading?: boolean;
  /**
   * Message to display when no data
   * @default 'No data available'
   */
  emptyMessage?: string;
  /**
   * Current search text — shown in empty state when a search yields no results
   */
  searchText?: string;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Row click handler
   */
  onRowClick?: (row: T) => void;
  /**
   * Row selection configuration
   */
  selection?: SelectionConfig<TId, T>;
  /**
   * Max height of the scroll container
   */
  maxHeight?: string | number | Record<string, string | number>;
  /**
   * Custom row component. Receives the standard TableRow props plus the `row` data.
   */
  rowComponent?: ComponentType<TableRowProps & { row: T }>;
}

// Type alias for backward compatibility with new naming
export type DataTableColumn<T> = ColumnDef<T>;

// ============================================================================
// Component
// ============================================================================

export function DataTable<T = any, TId = any>({
  columns,
  rows,
  onSort,
  sortColumn,
  sortOrder,
  loading = false,
  emptyMessage = 'No data available',
  searchText,
  className,
  onRowClick,
  selection,
  maxHeight,
  rowComponent,
}: DataTableProps<T, TId>) {
  const [hoveredRowId, setHoveredRowId] = useState<TId | null>(null);
  const [focusedRowId, setFocusedRowId] = useState<TId | null>(null);
  const [isHeaderHovered, setIsHeaderHovered] = useState(false);
  const [hoveredResizeColumn, setHoveredResizeColumn] = useState<string | null>(null);
  const [activeResizeColumn, setActiveResizeColumn] = useState<string | null>(null);
  const [columnWidths, setColumnWidths] = useState<Record<string, number | undefined>>({});
  const resizeCleanupRef = useRef<(() => void) | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const tableBackground = theme.palette.background.paper;
  const headerBackground = ((theme.palette.background as unknown as Record<string, unknown>)['elevated'] as string | undefined) ?? theme.palette.background.paper;
  const headerBorderColor = alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.16 : 0.12);
  const verticalDividerColor = alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.14 : 0.08);
  const activeResizeDividerColor = alpha(theme.palette.primary.main, 0.24);
  const zebraRowBackground = theme.palette.background.tableZebra ?? (theme.palette.mode === 'dark' ? '#171a22' : '#f7f8fa');
  const selectedRowBackground = theme.palette.background.tableSelected ?? (theme.palette.mode === 'dark' ? '#1c2b4a' : '#e7f0ff');

  const handleSort = (columnKey: string) => {
    if (onSort) {
      onSort(columnKey);
    }
  };

  const getSortDirection = (columnKey: string): 'asc' | 'desc' | false => {
    if (sortColumn === columnKey) {
      return sortOrder ?? 'asc';
    }
    return false;
  };

  const renderCell = (column: ColumnDef<T>, row: T) => {
    const value = row[column.key as keyof T];

    if (column.render) {
      return column.render(value, row);
    }

    // Ensure we always pass a valid ReactNode to Typography
    const displayValue = value != null ? String(value) : '-';
    return (
      <Typography variant="body2" noWrap>
        {displayValue}
      </Typography>
    );
  };

  // Get row ID helper
  const getRowId = (row: T, index: number): TId => {
    if (selection?.getRowId) {
      return selection.getRowId(row);
    }
    const fallbackId = (row as { id?: unknown } | null | undefined)?.id;
    return (fallbackId ?? index) as TId;
  };

  const isRowSelectable = useCallback(
    (row: T): boolean => {
      if (!selection?.enabled) {
        return false;
      }
      return selection.isRowSelectable ? selection.isRowSelectable(row) : true;
    },
    [selection]
  );

  const anySelected = useMemo(() => (selection ? selection.selectedIds.size > 0 : false), [selection]);
  const hasSelectableRows = useMemo(() => rows.some(row => isRowSelectable(row)), [rows, isRowSelectable]);
  const showHeaderCheckbox = isMobile || anySelected || isHeaderHovered;
  const selectionWidth = selection?.rowActions ? 84 : 48;
  const shouldRenderHoverOnlyColumns = selection?.enabled ? anySelected : true;
  const visibleColumns = useMemo(() => columns.filter(column => !column.showOnHoverOnly || shouldRenderHoverOnlyColumns), [columns, shouldRenderHoverOnlyColumns]);

  useEffect(
    () => () => {
      if (resizeCleanupRef.current) {
        resizeCleanupRef.current();
      }
    },
    []
  );

  useEffect(() => {
    setColumnWidths((prev: Record<string, number | undefined>) => {
      const visibleKeys = new Set(visibleColumns.map((col: ColumnDef<T>) => col.key));
      const next: Record<string, number | undefined> = {};

      Object.entries(prev).forEach(([key, value]) => {
        if (visibleKeys.has(key)) {
          next[key] = value;
        }
      });

      // Keep the previous reference when nothing was filtered out, to avoid a re-render.
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
  }, [visibleColumns]);

  const showLoadingOverlay = loading && rows.length > 0;

  if (!loading && rows.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
          px: 2,
          gap: 0.5,
        }}
      >
        <Typography variant="h6" color="text.secondary">
          {emptyMessage}
        </Typography>
        {searchText && searchText.trim().length > 0 && (
          <Typography variant="body2" color="text.disabled">
            No results for &ldquo;{searchText.trim()}&rdquo;
          </Typography>
        )}
      </Box>
    );
  }

  const getDefaultMinWidth = (column: ColumnDef<T>) => {
    if (column.minWidth != null) {
      return column.minWidth;
    }

    const label = column.label;
    // Approximate title text width + cell paddings (+ sort icon space for sortable columns)
    const estimatedLabelWidth = Math.ceil(label.length * 8 + 32 + (column.sortable ? 28 : 0));
    return Math.max(estimatedLabelWidth, 80);
  };

  const getResolvedWidth = (column: ColumnDef<T>) => {
    const manualWidth = columnWidths[column.key];
    if (manualWidth === undefined) return undefined;

    const minWidth = getDefaultMinWidth(column);
    const maxWidth = column.maxWidth ?? Number.POSITIVE_INFINITY;
    return Math.max(minWidth, Math.min(manualWidth, maxWidth));
  };

  const getColumnWidthSx = (column: ColumnDef<T>) => {
    const minWidth = getDefaultMinWidth(column);
    const resolvedWidth = getResolvedWidth(column);

    if (resolvedWidth != null) {
      return {
        minWidth: resolvedWidth,
        width: resolvedWidth,
        maxWidth: resolvedWidth,
      };
    }

    return {
      minWidth,
      ...(column.maxWidth ? { maxWidth: column.maxWidth } : {}),
    };
  };

  const handleResizeStart = (event: MouseEvent<HTMLSpanElement>, column: ColumnDef<T>) => {
    if (isMobile) return;

    event.preventDefault();
    event.stopPropagation();

    if (resizeCleanupRef.current) {
      resizeCleanupRef.current();
    }

    setActiveResizeColumn(column.key);

    const minWidth = getDefaultMinWidth(column);
    const maxWidth = column.maxWidth ?? Number.POSITIVE_INFINITY;
    const headerCellWidth = event.currentTarget.parentElement?.getBoundingClientRect().width;
    const startWidth = getResolvedWidth(column) ?? headerCellWidth ?? minWidth;
    const startX = event.clientX;

    const handleMouseMove = (moveEvent: globalThis.MouseEvent) => {
      const delta = moveEvent.clientX - startX;
      const nextWidth = Math.max(minWidth, Math.min(startWidth + delta, maxWidth));
      setColumnWidths(prev => (prev[column.key] === nextWidth ? prev : { ...prev, [column.key]: nextWidth }));
    };

    const cleanup = () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', cleanup);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setActiveResizeColumn(null);
      resizeCleanupRef.current = null;
    };

    resizeCleanupRef.current = cleanup;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', cleanup);
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <TableContainer
        sx={{
          bgcolor: tableBackground,
          height: 'auto',
          minHeight: 0,
          maxHeight: maxHeight ?? 'none',
          overflow: 'visible',
        }}
        {...(className !== undefined && { className })}
      >
        <Table
          size="small"
          sx={{
            tableLayout: 'auto',
            bgcolor: tableBackground,
            '& .MuiTableRow-root': {
              height: 44,
            },
            '& .MuiTableCell-head': {
              py: 0.7,
              verticalAlign: 'middle',
            },
            '& .MuiTableCell-body': {
              py: 0.55,
              verticalAlign: 'middle',
            },
          }}
        >
          <TableHead
            onMouseEnter={() => setIsHeaderHovered(true)}
            onMouseLeave={() => setIsHeaderHovered(false)}
            sx={{
              '& .MuiTableCell-head': {
                border: 0,
                bgcolor: `${headerBackground} !important`,
                borderBottom: '1px solid',
                borderBottomColor: headerBorderColor,
              },
            }}
          >
            <TableRow>
              {selection?.enabled && (
                <TableCell
                  sx={{
                    width: selectionWidth,
                    minWidth: selectionWidth,
                    maxWidth: selectionWidth,
                    p: 0,
                    position: 'relative',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      opacity: showHeaderCheckbox ? 1 : 0,
                      transition: 'opacity 0.15s ease',
                      '&:focus-within': { opacity: 1 },
                    }}
                  >
                    <Checkbox
                      size="small"
                      inputProps={{ 'aria-label': 'Select all rows' }}
                      indeterminate={selection.selectedIds.size > 0 && !selection.isAllSelected}
                      checked={selection.isAllSelected}
                      onChange={selection.onToggleAll}
                      icon={<CheckBoxOutlineBlankRoundedIcon fontSize="small" />}
                      checkedIcon={<CheckBoxRoundedIcon fontSize="small" />}
                      indeterminateIcon={<CheckBoxOutlineBlankRoundedIcon fontSize="small" />}
                      disabled={!hasSelectableRows}
                    />
                  </Box>
                </TableCell>
              )}
              {visibleColumns.map((column: ColumnDef<T>, columnIndex: number) => (
                <TableCell
                  key={column.key}
                  align={column.align ?? 'left'}
                  sx={{
                    ...getColumnWidthSx(column),
                    position: 'relative',
                    pr: 2,
                    boxShadow:
                      columnIndex < visibleColumns.length - 1
                        ? hoveredResizeColumn === column.key || activeResizeColumn === column.key
                          ? `inset -2px 0 0 ${activeResizeDividerColor}`
                          : 'inset -2px 0 0 transparent'
                        : 'none',
                    transition: 'box-shadow 0.12s ease',
                    py: 1.1,
                    fontSize: '0.74rem',
                    fontWeight: 700,
                    color: 'text.secondary',
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    overflow: 'visible',
                    '& .MuiTableSortLabel-root': {
                      maxWidth: '100%',
                      fontSize: 'inherit',
                      fontWeight: 'inherit',
                      color: 'inherit',
                    },
                    '& .MuiTableSortLabel-icon': {
                      display: 'none !important',
                    },
                    ...(isMobile &&
                      selection?.enabled &&
                      column.key === 'action' && {
                        position: 'sticky',
                        top: 'auto !important',
                        left: 0,
                        zIndex: 6,
                        bgcolor: `${headerBackground} !important`,
                        boxShadow: `1px 0 0 ${verticalDividerColor}`,
                      }),
                  }}
                >
                  {column.sortable && onSort ? (
                    <TableSortLabel active={sortColumn === column.key} direction={getSortDirection(column.key) || 'asc'} onClick={() => handleSort(column.key)} hideSortIcon>
                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, maxWidth: '100%' }}>
                        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {column.label}
                        </Box>
                        {sortColumn === column.key ? (
                          sortOrder === 'desc' ? (
                            <ArrowDownwardRoundedIcon sx={{ fontSize: 14, opacity: 0.85 }} />
                          ) : (
                            <ArrowUpwardRoundedIcon sx={{ fontSize: 14, opacity: 0.85 }} />
                          )
                        ) : (
                          <UnfoldMoreRoundedIcon sx={{ fontSize: 14, opacity: 0.5 }} />
                        )}
                      </Box>
                    </TableSortLabel>
                  ) : (
                    <Box component="span" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {column.label}
                    </Box>
                  )}
                  {!isMobile && columnIndex > 0 && (
                    <Box
                      component="span"
                      aria-hidden="true"
                      onMouseDown={event => handleResizeStart(event, visibleColumns[columnIndex - 1])}
                      onMouseEnter={() => setHoveredResizeColumn(visibleColumns[columnIndex - 1].key)}
                      onMouseLeave={() => setHoveredResizeColumn(current => (current === visibleColumns[columnIndex - 1].key ? null : current))}
                      sx={{ position: 'absolute', top: 0, left: 0, width: 6, height: '100%', cursor: 'col-resize', zIndex: 1 }}
                    />
                  )}
                  {!isMobile && columnIndex < visibleColumns.length - 1 && (
                    <Box
                      component="span"
                      role="separator"
                      tabIndex={0}
                      aria-orientation="vertical"
                      aria-label={`Resize column ${column.label}`}
                      aria-valuenow={getResolvedWidth(column) ?? getDefaultMinWidth(column)}
                      aria-valuemin={getDefaultMinWidth(column)}
                      {...(column.maxWidth !== undefined ? { 'aria-valuemax': column.maxWidth } : {})}
                      onKeyDown={(event: React.KeyboardEvent<HTMLSpanElement>) => {
                        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
                          event.preventDefault();
                          event.stopPropagation();
                          const minWidth = getDefaultMinWidth(column);
                          const maxWidth = column.maxWidth ?? Number.POSITIVE_INFINITY;
                          const headerCellWidth = event.currentTarget.parentElement?.getBoundingClientRect().width;
                          const startWidth = getResolvedWidth(column) ?? headerCellWidth ?? minWidth;
                          const delta = event.key === 'ArrowRight' ? 10 : -10;
                          const nextWidth = Math.max(minWidth, Math.min(startWidth + delta, maxWidth));
                          setColumnWidths(prev => (prev[column.key] === nextWidth ? prev : { ...prev, [column.key]: nextWidth }));
                        }
                      }}
                      onMouseDown={(event: MouseEvent<HTMLSpanElement>) => handleResizeStart(event, column)}
                      onMouseEnter={() => setHoveredResizeColumn(column.key)}
                      onMouseLeave={() => setHoveredResizeColumn((current: string | null) => (current === column.key ? null : current))}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: 6,
                        height: '100%',
                        cursor: 'col-resize',
                        zIndex: 1,
                        '&:focus-visible': {
                          outline: '2px solid',
                          outlineColor: 'primary.main',
                          outlineOffset: '-1px',
                          zIndex: 3,
                        },
                      }}
                    />
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 ? (
              <TableSkeleton rows={5} columns={visibleColumns.length + (selection?.enabled ? 1 : 0)} />
            ) : (
              rows.map((row, index) => {
                const rowId = getRowId(row, index);
                const rowSelectable = isRowSelectable(row);
                const isSelected = selection?.enabled ? selection.selectedIds.has(rowId) : false;
                const isHovered = hoveredRowId === rowId;
                const isFocused = focusedRowId === rowId;
                const showCheckboxControl = isMobile || isHovered || isSelected || anySelected;
                const showHoverOnlyContent = isMobile || isHovered || isFocused || (selection?.enabled ? anySelected : false);
                const rowBackgroundColor = isSelected ? selectedRowBackground : index % 2 === 0 ? zebraRowBackground : tableBackground;

                const rowProps: TableRowProps = {
                  hover: !!onRowClick,
                  ...(onRowClick ? { onClick: () => onRowClick(row) } : {}),
                  sx: {
                    cursor: onRowClick ? 'pointer' : 'default',
                    bgcolor: rowBackgroundColor,
                  },
                  ...(selection?.enabled ? { selected: isSelected } : {}),
                };

                const handleRowFocus = () => {
                  setFocusedRowId(rowId);
                };

                const handleRowBlur = (event: FocusEvent<HTMLTableRowElement>) => {
                  const nextTarget = event.relatedTarget;
                  if (nextTarget && event.currentTarget.contains(nextTarget as Node)) {
                    return;
                  }
                  setFocusedRowId(currentFocused => (currentFocused === rowId ? null : currentFocused));
                };

                const RowComp = (rowComponent ?? TableRow) as ComponentType<TableRowProps & { row?: T }>;

                return (
                  <RowComp
                    key={String(rowId)}
                    {...rowProps}
                    {...(rowComponent ? { row } : {})}
                    onMouseEnter={() => setHoveredRowId(rowId)}
                    onMouseLeave={() => setHoveredRowId(null)}
                    onFocus={handleRowFocus}
                    onBlur={handleRowBlur}
                  >
                    {selection?.enabled && (
                      <TableCell
                        sx={{
                          width: selectionWidth,
                          minWidth: selectionWidth,
                          maxWidth: selectionWidth,
                          p: 0,
                          position: 'relative',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.25,
                            height: '100%',
                            opacity: showCheckboxControl ? 1 : 0,
                            transition: 'opacity 0.15s ease',
                            '&:focus-within': { opacity: 1 },
                          }}
                          onClick={(e: MouseEvent) => e.stopPropagation()}
                        >
                          <Checkbox
                            size="small"
                            inputProps={{ 'aria-label': `Select row ${String(rowId)}` }}
                            checked={isSelected}
                            disabled={!rowSelectable}
                            onClick={(e: MouseEvent) => e.stopPropagation()}
                            onChange={() => {
                              if (!rowSelectable) return;
                              selection.onToggleOne(rowId);
                            }}
                          />
                          {selection.rowActions && <Box onClick={(e: MouseEvent) => e.stopPropagation()}>{selection.rowActions(row)}</Box>}
                        </Box>
                      </TableCell>
                    )}
                    {visibleColumns.map((column: ColumnDef<T>, columnIndex: number) => {
                      const isHoverOnly = !!column.showOnHoverOnly;
                      const showCellContent = !isHoverOnly || showHoverOnlyContent;
                      const rawValue = row[column.key as keyof T];
                      const cellTitle = typeof rawValue === 'string' || typeof rawValue === 'number' ? String(rawValue) : undefined;
                      const isLastColumn = columnIndex === visibleColumns.length - 1;

                      return (
                        <TableCell
                          key={column.key}
                          align={column.align ?? 'left'}
                          sx={{
                            ...getColumnWidthSx(column),
                            position: 'relative',
                            overflow: 'hidden',
                            boxShadow: !isLastColumn
                              ? hoveredResizeColumn === column.key || activeResizeColumn === column.key
                                ? `inset -2px 0 0 ${activeResizeDividerColor}`
                                : 'inset -2px 0 0 transparent'
                              : 'none',
                            transition: 'box-shadow 0.12s ease',
                            ...(isMobile &&
                              selection?.enabled &&
                              column.key === 'action' && {
                                position: 'sticky',
                                left: 0,
                                zIndex: 4,
                                bgcolor: `${rowBackgroundColor} !important`,
                                boxShadow: `1px 0 0 ${verticalDividerColor}`,
                              }),
                          }}
                          onClick={isHoverOnly ? (e: MouseEvent) => e.stopPropagation() : undefined}
                        >
                          <Box
                            title={cellTitle}
                            sx={{
                              display: 'block',
                              lineHeight: 1.4,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              ...(isHoverOnly && {
                                opacity: showCellContent ? 1 : 0,
                                visibility: showCellContent ? 'visible' : 'hidden',
                                pointerEvents: showCellContent ? 'auto' : 'none',
                                transition: 'opacity 0.15s ease',
                              }),
                            }}
                          >
                            {renderCell(column, row)}
                          </Box>
                          {!isMobile && columnIndex > 0 && (
                            <Box
                              component="span"
                              aria-hidden="true"
                              onMouseDown={(event: MouseEvent<HTMLSpanElement>) => handleResizeStart(event, visibleColumns[columnIndex - 1])}
                              onMouseEnter={() => setHoveredResizeColumn(visibleColumns[columnIndex - 1].key)}
                              onMouseLeave={() => setHoveredResizeColumn((current: string | null) => (current === visibleColumns[columnIndex - 1].key ? null : current))}
                              sx={{ position: 'absolute', top: 0, left: 0, width: 6, height: '100%', cursor: 'col-resize', zIndex: 2 }}
                            />
                          )}
                          {!isMobile && !isLastColumn && (
                            <Box
                              component="span"
                              aria-hidden="true"
                              onMouseDown={(event: MouseEvent<HTMLSpanElement>) => handleResizeStart(event, column)}
                              onMouseEnter={() => setHoveredResizeColumn(column.key)}
                              onMouseLeave={() => setHoveredResizeColumn((current: string | null) => (current === column.key ? null : current))}
                              sx={{ position: 'absolute', top: 0, right: 0, width: 6, height: '100%', cursor: 'col-resize', zIndex: 2 }}
                            />
                          )}
                        </TableCell>
                      );
                    })}
                  </RowComp>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {showLoadingOverlay && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.paper',
            opacity: 0.6,
            zIndex: 1,
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
}
